import { InvestmentDetailsInterface } from "../../models/fund/Deal";
import { ServiceError } from "../../service/Shared";
import { sentenceOrPascalToCamel } from "../common";

const getDealStructureValidationKey = (investmentKey: string, fieldKey: string): string =>
  `${investmentKey}.${fieldKey}`;

const normalisePathSegment = (segment: string): string => {
  const segmentMatch = segment.match(/^([^[]+)(?:\[(\d+)\])?$/);
  if (!segmentMatch) {
    return sentenceOrPascalToCamel(segment);
  }

  const [, segmentName, segmentIndex] = segmentMatch;
  const normalisedSegmentName = sentenceOrPascalToCamel(segmentName);

  return segmentIndex === undefined ? normalisedSegmentName : `${normalisedSegmentName}[${segmentIndex}]`;
};

const buildClientFieldPath = (pathSegments: string[]): string => {
  return pathSegments.reduce((fieldPath, segment) => {
    const normalisedSegment = normalisePathSegment(segment);

    return fieldPath ? `${fieldPath}.${normalisedSegment}` : normalisedSegment;
  }, "");
};

const getInvestmentFieldKeyFromPath = (
  path: string | undefined,
  investments: InvestmentDetailsInterface[],
  property?: string
): string | null => {
  if (!path) return null;

  const pathParts = path.split(".").filter(Boolean);
  if (pathParts.length < 2) return null;

  const [rootKey, instrumentWithIndex, ...fieldPathParts] = pathParts;

  if (rootKey !== "DealSpecificData") return null;

  const instrumentMatch = instrumentWithIndex.match(/^([^[]+)\[(\d+)\]$/);
  if (!instrumentMatch) return null;

  const instrumentKey = sentenceOrPascalToCamel(instrumentMatch[1]);
  const instrumentIndex = Number(instrumentMatch[2]);
  const targetInvestment = investments.filter((investment) => investment.instrument === instrumentKey)[instrumentIndex];

  if (!targetInvestment) return null;

  // eslint-disable-next-line no-nested-ternary
  const clientFieldPath = buildClientFieldPath(fieldPathParts.length > 0 ? fieldPathParts : property ? [property] : []);
  if (!clientFieldPath) return null;

  return getDealStructureValidationKey(targetInvestment.key, clientFieldPath);
};

const getDealStructureErrorFieldKeys = (error: ServiceError, investments: InvestmentDetailsInterface[]): string[] => {
  if (error.code === "VALIDATION_INVALID_ASSOCIATION") {
    return investments.map((investment) => getDealStructureValidationKey(investment.key, "associations"));
  }

  const fieldKeyFromPath = getInvestmentFieldKeyFromPath(error.path, investments, error.property);
  return fieldKeyFromPath ? [fieldKeyFromPath] : [];
};

export const getDealStructureValidationErrorFieldKeys = (
  errors: ServiceError[] | undefined,
  investments: InvestmentDetailsInterface[]
): Set<string> => new Set((errors ?? []).flatMap((error) => getDealStructureErrorFieldKeys(error, investments)));

export const hasDealStructureValidationError = (
  errorFieldKeys: Set<string>,
  investmentKey: string | undefined,
  fieldKey: string
): boolean => {
  if (!investmentKey) return false;

  return errorFieldKeys.has(getDealStructureValidationKey(investmentKey, fieldKey));
};
